import { useCallback, useEffect, useMemo, useState } from "react";

import type { FormControlLabelProps } from "@mui/material";
import { Divider, FormControlLabel } from "@mui/material";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import type { TextFieldProps } from "@mui/material/TextField";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { usePrevious } from "react-use";

import { MetricRangeType } from "@waterly/graphql-types";

import { useAdvancedToggle } from "../../../../hooks";
import type { MetricRangeCardMetricRange } from "../metricRangeUtils";
import { buildRangeExpression, getInequalitySymbol, InequalityOperator } from "../metricRangeUtils";

import { type MetricRangeTagOption } from "./MetricRangeTagSelect";

//TODO: Uncomment these lines when we are ready for the added functionality of notes, more tagging etc.

export type LowerUpperLimitConfigInputProps = {
  metricRangeType: MetricRangeType.Upper | MetricRangeType.Lower;
  range: MetricRangeCardMetricRange;
  metricRangeTagOptions: MetricRangeTagOption[];
  onChange: (range: MetricRangeCardMetricRange) => void;
  bordersIdeal: boolean;
  min: number;
  max: number;
  devMode?: boolean;
  disableRegulatory?: boolean;
  errorMessage?: string;
  disableOperational?: boolean;
};

const LowerUpperLimitConfigInput = ({
  metricRangeType,
  range,
  metricRangeTagOptions,
  onChange,
  bordersIdeal,
  min,
  max,
  devMode,
  disableRegulatory,
  disableOperational: _,
  errorMessage,
}: LowerUpperLimitConfigInputProps) => {
  const { t } = useTranslation(["common"]);
  const theme = useTheme();
  const isLower = metricRangeType === MetricRangeType.Lower;
  const limitName = t("common:limit");
  const tagId = metricRangeType === MetricRangeType.Upper ? range.minTagId : range.maxTagId;
  /*TODO: see todo note at top of file*/
  // const tagSelectType =
  //   metricRangeType === MetricRangeType.Upper ? MetricRangeType.Lower : MetricRangeType.Upper;
  const tagSelectValue = tagId ?? null;
  const lowerTag = metricRangeTagOptions.find((mrto) => mrto.id === range.minTagId)?.displayName;
  const upperTag = metricRangeTagOptions.find((mrto) => mrto.id === range.maxTagId)?.displayName;
  // const displayTag = metricRangeTagOptions.find((mrto) => mrto.id === tagId);

  const previousMin = usePrevious(min);
  const previousMax = usePrevious(max);

  const minSymbol = getInequalitySymbol(InequalityOperator.LESS_THAN, range.rangeMinInclusive);
  const maxSymbol = getInequalitySymbol(InequalityOperator.LESS_THAN, range.rangeMaxInclusive);
  const inputMinMaxText = `${t("common:input_range")}: ${min} ${minSymbol} x ${maxSymbol} ${max}`;
  const rangeMinMaxText = `${t("common:range")}: ${buildRangeExpression(range)}`;
  const minMaxTag = `${lowerTag} ${minSymbol} x ${maxSymbol} ${upperTag}`;

  const [showOptions, { toggle: _toggleOptions }] = useAdvancedToggle(false);
  const [limit, setLimit] = useState<number | null>(
    isLower ? range.rangeMax ?? null : range.rangeMin ?? null,
  );

  const updateParentStateIfValid = useCallback(
    (value: number | null, commitValue = true) => {
      if (commitValue) {
        onChange({
          ...range,
          ...(isLower ? { rangeMax: value } : { rangeMin: value }),
        });
      }
    },
    [isLower, onChange, range],
  );

  const handleLimitOnChange = useCallback<Required<TextFieldProps>["onChange"]>(
    (event) => {
      const proposedValue = event.target.value;
      const nextValue = proposedValue.trim();

      const nextRangeValue = nextValue === "" ? null : Number(nextValue);

      setLimit(nextRangeValue);
      updateParentStateIfValid(nextRangeValue);
    },
    [updateParentStateIfValid],
  );

  const regulatoryTag = useMemo(() => {
    return metricRangeTagOptions.find((tag) => tag.additionalProps.isRegulatory);
  }, [metricRangeTagOptions]);

  const regulatoryCheckboxOnChange = useCallback<Required<FormControlLabelProps>["onChange"]>(
    (_event, checked) => {
      const fieldToUpdate = metricRangeType === MetricRangeType.Lower ? "maxTagId" : "minTagId";
      const limitFieldToUpdate = isLower ? "rangeMax" : "rangeMin";
      const value = checked ? regulatoryTag?.id : null;
      onChange({ ...range, [limitFieldToUpdate]: limit, [fieldToUpdate]: value });
    },
    [isLower, limit, metricRangeType, onChange, range, regulatoryTag?.id],
  );

  /*TODO: see todo note at top of file*/
  // const handleTagSelect = useCallback(
  //   (type: MetricRangeType): MetricRangeTagSelectProps["onChange"] =>
  //     (value) => {
  //      const fieldToUpdate = type === MetricRangeType.Lower ? "minTagId" : "maxTagId";
  //      onChange({ ...range, [fieldToUpdate]: value?.id });
  //     },
  //   [onChange, range],
  // );

  /*TODO: see todo note at top of file*/
  // const getOptionDisabled = useCallback<Required<MetricRangeTagSelectProps>["getOptionDisabled"]>(
  //   (option) => {
  //     const disableReg = Boolean(disableRegulatory && option.additionalProps.isRegulatory);
  //     const disableOp = Boolean(disableOperational && option.additionalProps.isOperational);
  //     if (disableReg) return true;
  //     return disableOp;
  //   },
  //   [disableOperational, disableRegulatory],
  // );

  //keeps state synced
  useEffect(() => {
    if (isLower) setLimit(range.rangeMax ?? null);
  }, [isLower, range.rangeMax]);

  //keeps state synced
  useEffect(() => {
    if (!isLower) setLimit(range.rangeMin ?? null);
  }, [isLower, range.rangeMin]);

  //If an adjacent range is changed, this one might become valid, if so, lets update the parent state
  useEffect(() => {
    const boundsChanged =
      previousMin !== undefined && previousMax !== undefined && (previousMin !== min || previousMax !== max);

    if (boundsChanged) {
      const rangeLimit = isLower ? range.rangeMax ?? null : range.rangeMin ?? null;
      updateParentStateIfValid(limit, limit !== rangeLimit);
    }
  }, [
    isLower,
    limit,
    max,
    min,
    previousMax,
    previousMin,
    range.rangeMax,
    range.rangeMin,
    updateParentStateIfValid,
  ]);

  // This is temp, this will be removed
  const regulatoryCheckBox = useMemo(() => {
    return (
      <FormControlLabel
        checked={Boolean(tagSelectValue)}
        disabled={disableRegulatory}
        onChange={regulatoryCheckboxOnChange}
        control={<Checkbox />}
        label={regulatoryTag?.displayName}
      />
    );
  }, [disableRegulatory, regulatoryCheckboxOnChange, regulatoryTag?.displayName, tagSelectValue]);

  const labelWithTag = useMemo(() => {
    return (
      <Box display="flex">
        <Box maxWidth="15rem">
          <Typography>
            {`${limitName}: `} {bordersIdeal && limit}
          </Typography>
          {/*TODO: see todo note at top of file*/}
          {/*{!showOptions && displayTag && <Chip label={displayTag.displayName} />}*/}
          {showOptions && devMode && (
            <Stack>
              <Typography variant="caption">{`${inputMinMaxText}`}</Typography>
              <Typography variant="caption">{`${minMaxTag}`}</Typography>
              <Typography variant="caption">{`${rangeMinMaxText}`}</Typography>
            </Stack>
          )}
        </Box>
      </Box>
    );
  }, [limitName, bordersIdeal, limit, showOptions, devMode, inputMinMaxText, minMaxTag, rangeMinMaxText]);

  const entryField = useMemo(() => {
    return (
      <TextField
        fullWidth
        error={Boolean(errorMessage)}
        helperText={errorMessage}
        type="number"
        value={limit ?? ""}
        onChange={handleLimitOnChange}
      />
    );
  }, [errorMessage, handleLimitOnChange, limit]);

  /*TODO: see todo note at top of file*/
  // const editButton = useMemo(() => {
  //   return (
  //     <IconButton onClick={toggleOptions} size="small">
  //       <Build />
  //     </IconButton>
  //   );
  // }, [toggleOptions]);

  return (
    <>
      <Divider
        sx={{
          "&::before, &::after": {
            borderColor: errorMessage ? theme.palette.error.main : undefined,
          },
          color: errorMessage ? theme.palette.error.main : undefined,
          display: showOptions ? "none" : undefined,
        }}
        component="div"
        role="presentation"
      >
        <Box display="flex" justifyContent="center" alignItems="center" width="20rem" gap={2}>
          {labelWithTag}
          {!bordersIdeal && entryField}
          {regulatoryCheckBox}
          {/*TODO: see todo note at top of file*/}
          {/*{!showOptions && editButton}*/}
        </Box>
      </Divider>
      {/*TODO: see todo note at top of file*/}
      {/*<Collapse in={showOptions}>*/}
      {/*  <Box sx={{ p: 5, my: 2, border: `1px solid blue`, borderRadius: "15px" }}>*/}
      {/*    <Grid container spacing={2} alignItems="center">*/}
      {/*      <Grid size={{ xs: "auto" }}>{labelWithTag}</Grid>*/}
      {/*      {!bordersIdeal && <Grid size={{ xs: "grow" }}>{entryField}</Grid>}*/}
      {/*      {range.rangeMin !== range.rangeMax && (*/}
      {/*        <Grid size={{ xs: "grow" }}>*/}
      {/*          <MetricRangeTagSelect*/}
      {/*            value={tagSelectValue}*/}
      {/*            options={metricRangeTagOptions}*/}
      {/*            onChange={handleTagSelect(tagSelectType)}*/}
      {/*            getOptionDisabled={getOptionDisabled}*/}
      {/*          />*/}
      {/*        </Grid>*/}
      {/*      )}*/}
      {/*      <Grid size={{ xs: 1 }}>{editButton}</Grid>*/}
      {/*    </Grid>*/}
      {/*  </Box>*/}
      {/*</Collapse>*/}
    </>
  );
};

export default LowerUpperLimitConfigInput;
